import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useChat } from '../context/ChatContext';
import { dashboardApi } from '../services/dashboardApi';
import StatCard from '../components/StatCard';
import HealthSummaryCard from '../components/HealthSummaryCard';
import FaceRecognitionCard from '../components/FaceRecognitionCard';
import ScheduleOverviewCard from '../components/ScheduleOverviewCard';

const Dashboard = () => {
  const { user } = useAuth();
  const { conversations, loadConversations } = useChat();

  const [dashboardData, setDashboardData] = useState({
    healthData: null,
    faceData: null,
    scheduleData: null,
    activitySummary: null
  });

  const [loading, setLoading] = useState({
    health: true,
    faces: true,
    schedule: true,
    activity: true
  });

  const [errors, setErrors] = useState({
    health: null,
    faces: null,
    schedule: null,
    activity: null
  });

  const [refreshing, setRefreshing] = useState(false);

  // Load all dashboard data
  const loadDashboardData = async () => {
    setLoading({
      health: true,
      faces: true,
      schedule: true,
      activity: true
    });

    // Load health data
    try {
      const [recentRecords, healthSummary] = await Promise.all([
        dashboardApi.getRecentHealthRecords(5),
        dashboardApi.getHealthSummary()
      ]);

      setDashboardData(prev => ({
        ...prev,
        healthData: {
          recentRecords: recentRecords.data || [],
          summary: healthSummary.data || {}
        }
      }));
      setErrors(prev => ({ ...prev, health: null }));
    } catch (error) {
      console.error('Failed to load health data:', error);
      setErrors(prev => ({ ...prev, health: error }));
    } finally {
      setLoading(prev => ({ ...prev, health: false }));
    }

    // Load face recognition data
    try {
      const [faceStats, recentFaces] = await Promise.all([
        dashboardApi.getFaceStats(),
        dashboardApi.getRecentlyRecognizedFaces(5)
      ]);

      setDashboardData(prev => ({
        ...prev,
        faceData: {
          stats: faceStats.data || {},
          recentFaces: recentFaces.data || []
        }
      }));
      setErrors(prev => ({ ...prev, faces: null }));
    } catch (error) {
      console.error('Failed to load face data:', error);
      setErrors(prev => ({ ...prev, faces: error }));
    } finally {
      setLoading(prev => ({ ...prev, faces: false }));
    }

    // Load schedule data
    try {
      const scheduleOverview = await dashboardApi.getScheduleOverview();
      setDashboardData(prev => ({
        ...prev,
        scheduleData: scheduleOverview.data || {}
      }));
      setErrors(prev => ({ ...prev, schedule: null }));
    } catch (error) {
      console.error('Failed to load schedule data:', error);
      setErrors(prev => ({ ...prev, schedule: error }));
    } finally {
      setLoading(prev => ({ ...prev, schedule: false }));
    }

    // Load activity summary
    try {
      const activitySummary = await dashboardApi.getActivitySummary();
      setDashboardData(prev => ({
        ...prev,
        activitySummary: activitySummary.data || {}
      }));
      setErrors(prev => ({ ...prev, activity: null }));
    } catch (error) {
      console.error('Failed to load activity summary:', error);
      setErrors(prev => ({ ...prev, activity: error }));
    } finally {
      setLoading(prev => ({ ...prev, activity: false }));
    }
  };

  // Refresh all data
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    await loadConversations();
    setRefreshing(false);
  };

  // Load data on component mount
  useEffect(() => {
    loadConversations();
    loadDashboardData();
  }, []);

  // Get current time greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const activityData = dashboardData.activitySummary || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                {getGreeting()}! Welcome back, {user?.name || 'User'} 👋
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Here's an overview of your health, contacts, and schedule
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm w-full sm:w-auto justify-center"
              >
                <svg 
                  className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <div className="text-xs sm:text-sm text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Activity Summary Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <StatCard
            title="Health Analyses"
            value={loading.activity ? '...' : (activityData.healthAnalyses || 0)}
            change="12%"
            changeType="positive"
            color="green"
            subtitle="This month"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            }
          />

          <StatCard
            title="Faces Recognized"
            value={loading.activity ? '...' : (activityData.facesRecognized || 0)}
            change="8%"
            changeType="positive"
            color="purple"
            subtitle="Total recognitions"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
          />

          <StatCard
            title="Active Conversations"
            value={conversations.filter(c => !c.isArchived).length}
            change="3"
            changeType="positive"
            color="blue"
            subtitle="Chat sessions"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
          />

          <StatCard
            title="System Status"
            value="Active"
            color="indigo"
            subtitle="All systems operational"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        </div>

        {/* Main Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {/* Health Summary Card */}
          <div className="md:col-span-1">
            <HealthSummaryCard
              healthData={dashboardData.healthData}
              isLoading={loading.health}
              error={errors.health}
            />
          </div>

          {/* Face Recognition Card */}
          <div className="md:col-span-1">
            <FaceRecognitionCard
              faceData={dashboardData.faceData}
              isLoading={loading.faces}
              error={errors.faces}
            />
          </div>

          {/* Schedule Overview Card */}
          <div className="md:col-span-2 xl:col-span-1">
            <ScheduleOverviewCard
              scheduleData={dashboardData.scheduleData}
              isLoading={loading.schedule}
              error={errors.schedule}
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 sm:mt-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <Link 
                to="/health" 
                className="flex flex-col items-center p-3 sm:p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
              >
                <div className="p-2 sm:p-3 bg-green-100 rounded-full mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-900 text-center leading-tight">Upload Health Photo</span>
              </Link>

              <Link 
                to="/faces" 
                className="flex flex-col items-center p-3 sm:p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
              >
                <div className="p-2 sm:p-3 bg-purple-100 rounded-full mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-900 text-center">Register Face</span>
              </Link>

              <Link 
                to="/chat" 
                className="flex flex-col items-center p-3 sm:p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <div className="p-2 sm:p-3 bg-blue-100 rounded-full mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-900 text-center">Start Chat</span>
              </Link>

              <Link 
                to="/events" 
                className="flex flex-col items-center p-3 sm:p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                <div className="p-2 sm:p-3 bg-indigo-100 rounded-full mb-2">
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="text-xs sm:text-sm font-medium text-gray-900 text-center">Add Event</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Conversations */}
        {conversations.length > 0 && (
          <div className="mt-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Recent Conversations</h3>
                <Link
                  to="/chat"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View All
                </Link>
              </div>
              <div className="space-y-3">
                {conversations.slice(0, 3).map((conversation) => (
                  <Link
                    key={conversation._id}
                    to={`/chat/${conversation._id}`}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {conversation.title || 'New Conversation'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {conversation.messages?.length || 0} messages
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {new Date(conversation.updatedAt).toLocaleDateString()}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
